import { useMemo } from 'react';

import { Eye, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useConfigurations } from '@/hooks/useConfigurations';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useScenarioRuns } from '@/hooks/useScenarioRuns';
import { formatDateTime } from '@/utils/format';

import { RunCounts, ScenarioRunStatusBadge, targetLabel } from './badges';

export const ScenarioRunsListPage = () => {
  usePageTitle('Scenario Runs');
  const { data: runs, isLoading } = useScenarioRuns();
  const { data: configurations } = useConfigurations();

  const configNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of configurations ?? []) map.set(c.id, c.name);
    return map;
  }, [configurations]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <Card>
        <CardHeader>
          <CardTitle>Scenario Runs</CardTitle>
          <CardDescription>
            Suite runs of test scenarios against a configuration. Each run executes every assigned
            scenario on the chosen channels and judges the results.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!runs?.length ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              No scenario runs yet. Start one from a configuration&apos;s Scenarios section.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Created</TableHead>
                  <TableHead>Configuration</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Results</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.map((run) => (
                  <TableRow key={run.id}>
                    <TableCell className="text-muted-foreground text-xs">
                      {formatDateTime(run.created_at)}
                    </TableCell>
                    <TableCell className="text-xs">
                      <Link
                        to={`/configurations/${run.configuration_id}`}
                        className="text-primary hover:underline"
                      >
                        {configNames.get(run.configuration_id) ?? run.configuration_id.slice(0, 8)}
                      </Link>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{targetLabel(run)}</TableCell>
                    <TableCell>
                      <ScenarioRunStatusBadge status={run.status} />
                    </TableCell>
                    <TableCell>
                      <RunCounts run={run} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" asChild>
                        <Link to={`/agent-improvement/scenario-runs/${run.id}`}>
                          <Eye className="h-3.5 w-3.5" />
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
